import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { BodyText, Box, BrandingProductType, validateAndReturn } from 'vopay';
import { Title } from '#shared/components/UI';
import { useToast } from '#shared/providers/Toast';
import useFetchAccountBranding from '#shared/hooks/useApi/useFetchAccountBranding';
import useUpdateAccountBranding from '#shared/hooks/useApi/useUpdateAccountBranding';
import { BrandingHostProps } from './types';
import BrandingSettingsForm, { BrandingValues } from './BrandingForm/BrandingSettingsForm';
import useBrandingSettingsFormConfig from './BrandingForm/useBrandingSettingsFormConfig';
import PreviewSelector from './BrandingPreview/PreviewSelector';
import PreviewScreen from './BrandingPreview/PreviewScreen';
import buildPreviewOptions, {
  PreviewLabelKey,
  PreviewOption,
  SavedBrandingByProduct,
  usePreviewLabels,
} from './BrandingPreview/buildPreviewOptions.helper';
import useBrandingDiscardDialog from './useBrandingDiscardDialog';

const PRODUCT_LABEL_KEY: Partial<Record<BrandingProductType, PreviewLabelKey>> = {
  [BrandingProductType.Portal]: 'vopayPortal',
  [BrandingProductType.Elinx]: 'elinx',
  [BrandingProductType.Email]: 'emails',
  [BrandingProductType.Onboarding]: 'applicationForm',
};

export default function BrandingSettingsPage({
  products,
  defaultColors,
  readOnly = false,
  onPortalBrandingSaved,
}: BrandingHostProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Branding' });
  const previewLabels = usePreviewLabels();
  const toast = useToast();
  const confirmDiscard = useBrandingDiscardDialog();
  const { fetchAccountBranding } = useFetchAccountBranding();
  const { updateAccountBranding } = useUpdateAccountBranding();

  const DEFAULT_VALUES: BrandingValues = useMemo(
    () => ({
      Logo: '',
      SmallLogo: '',
      PrimaryColor: defaultColors.primary,
      SecondaryColor: defaultColors.secondary,
      ApplyToAll: false,
    }),
    [defaultColors.primary, defaultColors.secondary],
  );

  const previewOptions = useMemo(() => buildPreviewOptions(products), [products]);

  const [selectedPreviewKey, setSelectedPreviewKey] = useState('');

  // Resolved rather than stored, so the first option is selected once the locked-feature flags
  // resolve and the options actually exist.
  const selectedPreview = previewOptions.find((option) => option.key === selectedPreviewKey) ?? previewOptions[0];
  const productType = selectedPreview?.productType ?? BrandingProductType.Portal;

  // Every enabled product's saved branding: the selector tiles each render their own product's
  // values, so one product's edits never bleed onto another's tile.
  const [savedBranding, setSavedBranding] = useState<SavedBrandingByProduct>({});

  const defaultValues = savedBranding[productType] ?? DEFAULT_VALUES;

  const [isEditing, setIsEditing] = useState(false);

  const { ctx, renderFormElements, logoFields, colorFields, applyToAllField } = useBrandingSettingsFormConfig({
    productType,
    initialValues: defaultValues,
    isEditing,
    onSubmit: async (values) => {
      const saved = await handleSubmit(values);

      if (saved) setIsEditing(false);
    },
  });

  // Switching product re-initialises the form with that product's saved values, so drop out of
  // edit mode with it instead of carrying Edit/Cancel/Save over to a product left untouched.
  useEffect(() => {
    setIsEditing(false);
  }, [productType]);

  // While editing, the preview follows the form; otherwise it shows what is actually saved.
  const previewValues: BrandingValues = isEditing
    ? {
        ...ctx.values,
        PrimaryColor: validateAndReturn(defaultValues.PrimaryColor, ctx.values.PrimaryColor),
        SecondaryColor: validateAndReturn(defaultValues.SecondaryColor, ctx.values.SecondaryColor),
      }
    : defaultValues;

  // Each product is merged in as its own response lands rather than awaiting them all:
  // the form only needs the product being edited..
  async function fetchAllBranding() {
    const results = await Promise.all(
      products.map(async (productType) => {
        try {
          const res = await fetchAccountBranding({ Product: productType });

          setSavedBranding((current) => ({
            ...current,
            [productType]: {
              Logo: res.Logo,
              SmallLogo: res.SmallLogo,
              PrimaryColor: res.BrandingDetails?.PrimaryColor || DEFAULT_VALUES.PrimaryColor,
              SecondaryColor: res.BrandingDetails?.SecondaryColor || DEFAULT_VALUES.SecondaryColor,
              ApplyToAll: false,
            },
          }));

          return true;
        } catch {
          return false;
        }
      }),
    );

    if (results.includes(false)) {
      toast.neutral(t('fetchError', { defaultValue: 'Could not load branding settings.' }));
    }
  }

  useEffect(() => {
    fetchAllBranding();
  }, [products]);

  // Only a change of product discards anything — the eLinx and Bank Verify tiles share one
  // product, so moving between them just swaps the preview and leaves the form alone.
  async function handleSelectPreview(option: PreviewOption) {
    const switchesProduct = option.productType !== productType;

    if (switchesProduct && ctx.hasPendingData && !(await confirmDiscard())) return;

    setSelectedPreviewKey(option.key);
  }

  async function handleSubmit(values: BrandingValues): Promise<boolean> {
    const productsToSave = values.ApplyToAll ? products : [productType];

    const settled = await Promise.allSettled(
      productsToSave.map((product) =>
        updateAccountBranding({
          Product: product,
          Logo: values.Logo,
          SmallLogo: values.SmallLogo,
          PrimaryColor: values.PrimaryColor,
          SecondaryColor: values.SecondaryColor,
        }),
      ),
    );

    const failed = settled.filter((result) => result.status === 'rejected');

    if (failed.length > 0) {
      toast.neutral(t('Dialogue.errorTitle', { defaultValue: 'Error! Branding Settings not updated.' }));

      return false;
    }

    toast.success(t('Dialogue.successTitle', { defaultValue: 'Success! Branding Settings was updated' }));

    await fetchAllBranding();

    if (values.ApplyToAll || productType === BrandingProductType.Portal) {
      try {
        const portalRes = await fetchAccountBranding({ Product: BrandingProductType.Portal });

        onPortalBrandingSaved(portalRes);
      } catch {
        toast.neutral(t('fetchError', { defaultValue: 'Could not load branding settings.' }));
      }
    }

    return true;
  }

  return (
    <Box flx="col" gapSize="20px">
      {/* Leaving the page by a link or the browser's history buttons; Cancel and the product tiles ask on their own. */}
      <Prompt
        when={ctx.hasPendingData}
        message={t('leavePromptMessage', { defaultValue: 'You have unsaved branding data that will be discarded.' })}
      />

      <Box flx="col" gapSize="8px">
        <Title>{t('title', { defaultValue: 'Branding Settings' })}</Title>

        <BodyText style={{ color: 'rgba(45, 45, 45, 0.75)' }}>
          {t('description', {
            defaultValue:
              'Customize how your business appears to your customers when onboarding and receiving payouts.',
          })}
        </BodyText>
      </Box>

      {previewOptions.length > 1 && (
        <PreviewSelector
          options={previewOptions}
          selectedKey={selectedPreview.key}
          savedBranding={savedBranding}
          onSelect={handleSelectPreview}
        />
      )}

      <BrandingSettingsForm
        productType={productType}
        productLabel={previewLabels[PRODUCT_LABEL_KEY[productType] ?? 'vopayPortal']}
        readOnly={readOnly}
        isEditing={isEditing}
        onEdit={() => setIsEditing(true)}
        onCancel={() => setIsEditing(false)}
        ctx={ctx}
        renderFormElements={renderFormElements}
        logoFields={logoFields}
        colorFields={colorFields}
        applyToAllField={applyToAllField}
      />

      {selectedPreview && (
        <PreviewScreen option={selectedPreview} brandingValues={previewValues} isEditing={isEditing} />
      )}
    </Box>
  );
}
